import React, { useState, useEffect } from 'react';
import { tenantDoctorAPI } from '../../../services/api/tenantDoctorAPI';
import useEscKeyHandler from '../../../hooks/useEscKeyHandler';
import toast from 'react-hot-toast';
import { useAuth } from '../../../services/contexts/AuthContext';
import AddPatientModal from '../../../components/AddPatientModal';
import PatientVitalsModal from '../../../components/PatientVitalsModal';
import PatientDetailsModal from '../../../components/PatientDetailsModal';
import PatientHealthTrends from '../../../components/PatientHealthTrends';
import { 
  UserCheck, 
  Plus,
  Search, 
  Edit3, 
  Eye,
  Calendar,
  Mail,
  Phone,
  MapPin,
  Activity,
  Filter,
  Grid3X3,
  List,
  Send,
  MessageSquare,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react';

const PatientReview = () => {
  const { user } = useAuth();
  const [patients, setPatients] = useState([]);
  const [filteredPatients, setFilteredPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'grid' or 'list'
  const [showSendModal, setShowSendModal] = useState(false);
  const [sendingLink, setSendingLink] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPatient, setEditingPatient] = useState(null);
  const [showVitalsModal, setShowVitalsModal] = useState(false);
  const [selectedPatientForVitals, setSelectedPatientForVitals] = useState(null);
  const [showHealthTrendsModal, setShowHealthTrendsModal] = useState(false);
  const [selectedPatientForHealthTrends, setSelectedPatientForHealthTrends] = useState(null);
  const [patientLastRecordedDates, setPatientLastRecordedDates] = useState({});
  const [callingPatient, setCallingPatient] = useState(false);

  useEffect(() => {
    loadPatients();
  }, []);

  // Debug: Log user object when it changes
  useEffect(() => {
    console.log('PatientReview: user object changed:', user);
    console.log('PatientReview: user.tenant_id:', user?.tenant_id);
    console.log('PatientReview: user.role:', user?.role);
  }, [user]);

  useEffect(() => {
    filterPatients();
  }, [searchTerm, patients]);

  // Add Esc key handler for modal
  useEscKeyHandler(showModal, () => setShowModal(false));
  useEscKeyHandler(showSendModal, () => setShowSendModal(false));
  useEscKeyHandler(showEditModal, () => {
    setShowEditModal(false);
    setEditingPatient(null);
  });
  useEscKeyHandler(showHealthTrendsModal, () => {
    setShowHealthTrendsModal(false);
    setSelectedPatientForHealthTrends(null);
  });

  const loadPatients = async () => {
    try {
      setLoading(true);
      const response = await tenantDoctorAPI.getPatients();
      setPatients(response.data);
      
      // Load last vitals recording dates for each patient
      await loadPatientLastRecordedDates(response.data);
    } catch (error) {
      console.error('Failed to load patients:', error);
      toast.error('Failed to load patients');
    } finally {
      setLoading(false);
    }
  };

  const loadPatientLastRecordedDates = async (patientData) => {
    try {
      const lastRecordedDates = {};
      
      for (const patient of patientData) {
        try {
          // Get patient vitals to find the most recent recording
          const vitalsResponse = await tenantDoctorAPI.getPatientVitals(patient.id);
          if (vitalsResponse.data && vitalsResponse.data.length > 0) {
            // Sort by created_at to get the most recent
            const sortedVitals = vitalsResponse.data.sort((a, b) => 
              new Date(b.created_at) - new Date(a.created_at)
            );
            lastRecordedDates[patient.id] = sortedVitals[0].created_at;
          } else {
            lastRecordedDates[patient.id] = null;
          }
        } catch (error) {
          console.log(`No vitals found for patient ${patient.id}:`, error.message);
          lastRecordedDates[patient.id] = null;
        }
      }
      
      setPatientLastRecordedDates(lastRecordedDates);
      console.log('Last recorded dates loaded:', lastRecordedDates);
    } catch (error) {
      console.error('Failed to load patient last recorded dates:', error);
    }
  };

  const filterPatients = () => {
    if (!searchTerm) {
      setFilteredPatients(patients);
    } else {
      const filtered = patients.filter(patient =>
        patient.alias?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        patient.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        patient.name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredPatients(filtered);
    }
  };

  const handleViewPatient = (patient) => {
    setSelectedPatient(patient);
    setShowModal(true);
  };

  const handleSendLink = (patient) => {
    setSelectedPatient(patient);
    setShowSendModal(true);
  };

  const handleEditPatient = (patient) => {
    setEditingPatient(patient);
    setShowEditModal(true);
  };

  const handleViewVitals = (patient) => {
    setSelectedPatientForVitals(patient);
    setShowVitalsModal(true);
  };

  const handleViewHealthTrends = (patient) => {
    setSelectedPatientForHealthTrends(patient);
    setShowHealthTrendsModal(true);
  };

  const handleInitiateCall = async (patient) => {
    try {
      if (!patient.phone) {
        toast.error('Patient does not have a phone number');
        return;
      }
      
      setCallingPatient(true);
      toast.loading('Initiating call...');
      
      const response = await tenantDoctorAPI.initiatePatientCall(patient.id);
      
      toast.success(`Call initiated to ${patient.alias}`);
      console.log('Call SID:', response.data.call_sid);
      
    } catch (error) {
      console.error('Failed to initiate call:', error);
      toast.error(`Failed to initiate call: ${error.response?.data?.detail || error.message}`);
    } finally {
      setCallingPatient(false);
    }
  };

  const calculateAge = (dob) => {
    if (!dob) return 'N/A';
    const birthDate = new Date(dob);
    const today = new Date();
    const age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      return age - 1;
    }
    return age;
  };

  const PatientModal = () => {
    // If no patient is selected, show create patient form
    if (!selectedPatient) {
      return <AddPatientModal 
        onClose={() => setShowModal(false)} 
        onSubmit={handlePatientCreated} 
        currentTenantId={user?.tenant_id}
      />;
    }

    // Use the shared PatientDetailsModal component
    return (
      <PatientDetailsModal
        patient={selectedPatient}
        onClose={() => setShowModal(false)}
        onEdit={() => handleEditPatient(selectedPatient)}
        onSendLink={() => handleSendLink(selectedPatient)}
        onViewVitals={() => handleViewVitals(selectedPatient)}
        onCall={handleInitiateCall}
        userRole="doctor"
        apiClient={tenantDoctorAPI}
      />
    );
  };



  const SendLinkModal = () => {
    const [selectedChannel, setSelectedChannel] = useState('sms');
    const [customMessage, setCustomMessage] = useState('');

    const handleSend = async () => {
      try {
        setSendingLink(true);
        await tenantDoctorAPI.sendRecordingLink(selectedPatient.id, selectedChannel, customMessage);
        toast.success(`Recording link sent via ${selectedChannel} to ${selectedPatient.alias}`);
        setShowSendModal(false);
        setSelectedPatient(null);
        setShowModal(false); // Close the main patient details modal
      } catch (error) {
        console.error('Failed to send recording link:', error);
        toast.error(`Failed to send recording link via ${selectedChannel}`);
      } finally {
        setSendingLink(false);
      }
    };

    if (!selectedPatient) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-[#F9FBFC] rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-[#DCEAF5]">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-[#0B1C2C]">Send Recording Link</h2>
              <button
                onClick={() => setShowSendModal(false)}
                className="text-[#3D3D3D] hover:text-[#207CE5]"
              >
                <XCircle className="h-6 w-6" />
              </button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Patient Info */}
            <div className="bg-[#DCEAF5] p-4 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-br from-[#207CE5] to-[#4682B4] rounded-full flex items-center justify-center">
                  <span className="text-white font-bold">
                    {selectedPatient.alias?.split(' ').map(n => n[0]).join('') || 'P'}
                  </span>
                </div>
                <div>
                  <h3 className="font-semibold text-[#0B1C2C]">{selectedPatient.alias}</h3>
                  <p className="text-sm text-[#3D3D3D]">{selectedPatient.email}</p>
                  {selectedPatient.phone && (
                    <p className="text-sm text-[#3D3D3D]">{selectedPatient.phone}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Communication Channel */}
            <div>
              <label className="block text-sm font-medium text-[#3D3D3D] mb-3">
                Choose Communication Channel
              </label>
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => setSelectedChannel('sms')}
                  className={`p-4 border-2 rounded-lg flex items-center space-x-3 transition-colors ${
                    selectedChannel === 'sms'
                      ? 'border-[#207CE5] bg-[#DCEAF5] text-[#207CE5]'
                      : 'border-[#DCEAF5] hover:border-[#207CE5]'
                  }`}
                  disabled={!selectedPatient.phone}
                >
                  <MessageSquare className="h-6 w-6 flex-shrink-0" />
                  <div className="text-left min-w-0 flex-1">
                    <p className="font-medium">SMS</p>
                    <p className="text-sm text-[#3D3D3D] truncate">
                      {selectedPatient.phone ? (
                        <>
                          Send to<br />
                          {selectedPatient.phone}
                        </>
                      ) : (
                        'No phone number'
                      )}
                    </p>
                  </div>
                </button>

                <button
                  onClick={() => setSelectedChannel('email')}
                  className={`p-4 border-2 rounded-lg flex items-center space-x-3 transition-colors ${
                    selectedChannel === 'email'
                      ? 'border-[#207CE5] bg-[#DCEAF5] text-[#207CE5]'
                      : 'border-[#DCEAF5] hover:border-[#207CE5]'
                  }`}
                >
                  <Mail className="h-6 w-6 flex-shrink-0" />
                  <div className="text-left min-w-0 flex-1">
                    <p className="font-medium">Email</p>
                    <p className="text-sm text-[#3D3D3D] truncate">
                      Send to<br />
                      {selectedPatient.email}
                    </p>
                  </div>
                </button>
              </div>
            </div>

            {/* Custom Message */}
            <div>
              <label className="block text-sm font-medium text-[#3D3D3D] mb-2">
                Custom Message (Optional)
              </label>
              <textarea
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-[#DCEAF5] rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
                placeholder="Add a personal message to include with the recording link..."
              />
            </div>

            {/* Preview */}
            <div className="bg-[#DCEAF5] p-4 rounded-lg">
              <h4 className="font-medium text-[#0B1C2C] mb-2">Message Preview:</h4>
              <div className="text-sm text-[#3D3D3D]">
                <p className="font-medium">Subject: Your Vital Signs Recording Request</p>
                <div className="mt-2 space-y-1">
                  <p>Dear {selectedPatient.alias},</p>
                  <p>Please click the link below to record your vital signs using our secure system:</p>
                  <p className="font-mono bg-[#F9FBFC] px-2 py-1 rounded text-[#207CE5]">
                    [Recording Link - Valid for 10 minutes]
                  </p>
                  {customMessage && (
                    <>
                      <p className="mt-2 font-medium">Personal message:</p>
                      <p className="italic">"{customMessage}"</p>
                    </>
                  )}
                  <p className="mt-2">Best regards,<br />Your Healthcare Team</p>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-6 border-t border-[#DCEAF5]">
              <button
                onClick={() => setShowSendModal(false)}
                className="px-4 py-2 text-sm font-medium text-[#3D3D3D] bg-[#F9FBFC] border border-[#DCEAF5] rounded-lg hover:bg-[#DCEAF5]"
              >
                Cancel
              </button>
              <button
                onClick={handleSend}
                disabled={sendingLink || (!selectedPatient.email && selectedChannel === 'email') || (!selectedPatient.phone && selectedChannel === 'sms')}
                className="px-4 py-2 text-sm font-medium text-white bg-[#207CE5] border border-transparent rounded-lg hover:bg-[#4682B4] disabled:opacity-50 flex items-center space-x-2"
              >
                {sendingLink ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" />
                    <span>Send Recording Link</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const EditPatientModal = () => {
    if (!editingPatient) return null;

    // Get tenant_id from user object or try to get it from the current session
    const getCurrentTenantId = () => {
      if (user?.tenant_id) {
        return user.tenant_id;
      }
      
      // Fallback: try to get tenant_id from localStorage
      try {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          const parsedUser = JSON.parse(storedUser);
          console.log('EditPatientModal (Tenant Doctor): stored user from localStorage:', parsedUser);
          return parsedUser.tenant_id;
        }
      } catch (error) {
        console.error('Error parsing stored user:', error);
      }
      
      return null;
    };
    
    const currentTenantId = getCurrentTenantId();

    // Transform the patient data to match AddPatientModal format
    const patientDataForEdit = {
      id: editingPatient.id, // Include the patient ID for editing
      first_name: editingPatient.first_name || editingPatient.alias?.split(' ')[0] || '',
      last_name: editingPatient.last_name || editingPatient.alias?.split(' ').slice(1).join(' ') || '',
      email: editingPatient.email || '',
      phone: editingPatient.phone || '',
      dob: editingPatient.dob || '',
      gender: editingPatient.gender || '',
      address: editingPatient.address || '',
      emergency_contact_name: editingPatient.emergency_contact_name || '',
      emergency_contact_phone: editingPatient.emergency_contact_phone || '',
      medical_notes: editingPatient.medical_notes || ''
    };

    // If we have an alias but no first_name/last_name, split the alias
    if (editingPatient.alias && !editingPatient.first_name && !editingPatient.last_name) {
      const nameParts = editingPatient.alias.split(' ');
      patientDataForEdit.first_name = nameParts[0] || '';
      patientDataForEdit.last_name = nameParts.slice(1).join(' ') || '';
    }

    return (
      <AddPatientModal
        patient={patientDataForEdit}
        onClose={() => {
          setShowEditModal(false);
          setEditingPatient(null);
        }}
        onSubmit={async (updatedPatientData) => {
          try {
            console.log('Updating patient with data:', updatedPatientData);
            console.log('Patient ID:', editingPatient.id);
            
            // Call the API to update the patient
            const response = await tenantDoctorAPI.updatePatient(editingPatient.id, updatedPatientData);
            console.log('Update response:', response);
            
            toast.success('Patient updated successfully');
            setShowEditModal(false);
            setEditingPatient(null);
            
            // Refresh the patient list
            await loadPatients();
            console.log('Patients refreshed');
          } catch (error) {
            console.error('Failed to update patient:', error);
            toast.error(`Failed to update patient: ${error.message}`);
          }
        }}
        isEditing={true}
        currentTenantId={currentTenantId}
      />
    );
  };

  const handlePatientCreated = async (patientData) => {
    try {
      // Use tenantDoctorAPI to create the patient
      await tenantDoctorAPI.createPatient(patientData);
      toast.success('Patient created successfully');
      
      // Close modal and refresh the patients list
      setShowModal(false);
      loadPatients();
    } catch (error) {
      console.error('Failed to create patient:', error);
      toast.error('Failed to create patient');
    }
  };

  const refreshPatientLastRecordedDates = async () => {
    try {
      await loadPatientLastRecordedDates(patients);
    } catch (error) {
      console.error('Failed to refresh patient last recorded dates:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#207CE5]"></div>
      </div>
    );
  }

  // Check if user is properly loaded and has tenant_id
  if (!user || !user.tenant_id) {
    console.error('User not loaded or missing tenant_id:', user);
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Authentication Error</h3>
          <p className="text-gray-600 mb-4">
            Unable to load user information. Please log out and log back in.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Refresh Page
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#0B1C2C]">Patient Management</h1>
          <p className="text-[#3D3D3D]">Manage patients within your organization</p>
        </div>
        
        <div className="flex space-x-3">
          <button 
            onClick={() => {
              setSelectedPatient(null);
              setShowModal(true);
            }}
            className="flex items-center space-x-2 px-4 py-2 bg-[#207CE5] text-white rounded-lg hover:bg-[#4682B4] transition-colors"
          >
            <Plus className="h-4 w-4" />
            <span>Add New Patient</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-[#F9FBFC] p-6 rounded-xl shadow-sm border border-[#DCEAF5]">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-[#DCEAF5] mr-4">
              <UserCheck className="h-6 w-6 text-[#207CE5]" />
            </div>
            <div>
              <p className="text-sm font-medium text-[#3D3D3D]">Total Patients</p>
              <p className="text-2xl font-bold text-[#0B1C2C]">{patients.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-[#F9FBFC] p-6 rounded-xl shadow-sm border border-[#DCEAF5]">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-[#DCEAF5] mr-4">
              <Activity className="h-6 w-6 text-[#008080]" />
            </div>
            <div>
              <p className="text-sm font-medium text-[#3D3D3D]">Active Patients</p>
              <p className="text-2xl font-bold text-[#0B1C2C]">
                {patients.filter(p => p.status === 'active').length}
              </p>
            </div>
          </div>
        </div>

        {/* <div className="bg-[#F9FBFC] p-6 rounded-xl shadow-sm border border-[#DCEAF5]">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-[#DCEAF5] mr-4">
              <Heart className="h-6 w-6 text-[#00BCD4]" />
            </div>
            <div>
              <p className="text-sm font-medium text-[#3D3D3D]">Recordings This Month</p>
              <p className="text-2xl font-bold text-[#0B1C2C]">47</p>
            </div>
          </div>
        </div> */}

        {/* <div className="bg-[#F9FBFC] p-6 rounded-xl shadow-sm border border-[#DCEAF5]">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-[#DCEAF5] mr-4">
              <TrendingUp className="h-6 w-6 text-[#4682B4]" />
            </div>
            <div>
              <p className="text-sm font-medium text-[#3D3D3D]">Growth Rate</p>
              <p className="text-2xl font-bold text-[#0B1C2C]">+12%</p>
            </div>
          </div>
        </div> */}
      </div>

      {/* Search and Filters */}
      <div className="bg-[#F9FBFC] rounded-xl shadow-sm border border-[#DCEAF5] p-6">
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#3D3D3D]" />
            <input
              type="text"
              placeholder="Search patients by name, alias, or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-[#DCEAF5] rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
            />
          </div>
          <button className="flex items-center space-x-2 px-4 py-2 border border-[#DCEAF5] rounded-lg hover:bg-[#DCEAF5]">
            <Filter className="h-4 w-4" />
            <span>Filter</span>
          </button>
          <div className="flex items-center border border-[#DCEAF5] rounded-lg overflow-hidden">
            <button
              onClick={() => setViewMode('grid')}
              className={`px-3 py-2 text-sm font-medium transition-colors ${
                viewMode === 'grid'
                  ? 'bg-[#207CE5] text-white'
                  : 'bg-[#F9FBFC] text-[#3D3D3D] hover:bg-[#DCEAF5]'
              }`}
              title="Grid View"
            >
              <Grid3X3 className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-2 text-sm font-medium transition-colors ${
                viewMode === 'list'
                  ? 'bg-[#207CE5] text-white'
                  : 'bg-[#F9FBFC] text-[#3D3D3D] hover:bg-[#DCEAF5]'
              }`}
              title="List View"
            >
              <List className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Patients View */}
      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPatients.map((patient) => (
            <div key={patient.id} className="bg-[#F9FBFC] rounded-xl shadow-sm border border-[#DCEAF5] overflow-hidden hover:shadow-md transition-shadow">
              <div className="p-6">
                <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-[#207CE5] to-[#4682B4] rounded-full flex items-center justify-center">
                      <span className="text-white font-bold">
                        {patient.alias?.split(' ').map(n => n[0]).join('') || 'P'}
                      </span>
                    </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-[#0B1C2C] truncate">{patient.alias}</h3>
                      <p className="text-sm text-[#3D3D3D]">
                        {patient.gender && patient.dob ? 
                          `${patient.gender}, ${calculateAge(patient.dob)} years` : 
                          'Information incomplete'
                        }
                      </p>
                    </div>
                </div>

                {/* Latest Vitals Summary */}
                {patient.latest_vital ? (
                  <div className="mb-4 p-3 bg-[#F9FBFC] rounded-lg border border-[#DCEAF5]">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-[#0B1C2C]">Latest Assessment</span>
                      <span className="text-xs text-[#3D3D3D]">
                        {new Date(patient.latest_vital.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-[#3D3D3D]">HR:</span>
                        <span className="ml-1 font-medium text-[#0B1C2C]">{patient.latest_vital.heart_rate || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-[#3D3D3D]">Rhythm:</span>
                        <span className={`ml-1 font-medium ${
                          patient.latest_vital.rhythm && ['Irregular', 'Atrial Fibrillation'].includes(patient.latest_vital.rhythm) 
                            ? 'text-[#207CE5]' 
                            : 'text-[#0B1C2C]'
                        }`}>
                          {patient.latest_vital.rhythm || 'Normal'}
                        </span>
                      </div>
                    </div>
                    
                    {/* Clinical Alert */}
                    {patient.latest_vital.rhythm && ['Irregular', 'Atrial Fibrillation'].includes(patient.latest_vital.rhythm) && (
                      <div className="mt-2 p-2 bg-[#F9FBFC] border border-[#207CE5] rounded">
                        <div className="flex items-center space-x-1">
                          <span className="h-3 w-3 text-[#207CE5]">⚠️</span>
                          <span className="text-xs font-medium text-[#207CE5]">Requires clinical review</span>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="mb-4 p-3 bg-[#F9FBFC] border border-[#00BCD4] rounded-lg">
                    <div className="flex items-center space-x-2">
                      <Clock className="h-4 w-4 text-[#00BCD4]" />
                      <span className="text-sm text-[#00BCD4]">No recent vitals recorded</span>
                    </div>
                  </div>
                )}

                                 {/* Contact Info */}
                 <div className="space-y-2 mb-4">
                   {/* <div className="flex items-center text-sm text-[#3D3D3D]">
                     <Mail className="h-4 w-4 mr-2 flex-shrink-0" />
                     <span className="truncate">{patient.email}</span>
                   </div> */}
                   {patient.phone && (
                     <div className="flex items-center text-sm text-[#3D3D3D]">
                       <Phone className="h-4 w-4 mr-2 flex-shrink-0" />
                       <span>{patient.phone}</span>
                     </div>
                   )}
                 </div>

                <div className="mt-4 space-y-2">
                  <div className="flex items-center text-sm text-[#3D3D3D]">
                    <Calendar className="h-4 w-4 mr-2 flex-shrink-0" />
                    <span>
                      Last Recorded: {patientLastRecordedDates[patient.id] 
                        ? new Date(patientLastRecordedDates[patient.id]).toLocaleDateString() 
                        : 'No recordings yet'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-[#3D3D3D]">Recording</span>
                    <button 
                      onClick={() => handleSendLink(patient)}
                      className="px-3 py-1 text-xs font-medium text-white bg-[#207CE5] rounded-lg hover:bg-[#4682B4] transition-colors"
                    >
                      Send Link
                    </button>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-[#3D3D3D]">Calling</span>
                    <button 
                      onClick={() => handleInitiateCall(patient)}
                      disabled={!patient.phone || callingPatient}
                      className="px-3 py-1 text-xs font-medium text-white bg-[#008080] rounded-lg hover:bg-[#00BCD4] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {callingPatient ? 'Calling...' : 'Call'}
                    </button>
                  </div>
                </div>

                <div className="mt-4 flex items-center justify-between">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#DCEAF5] text-[#008080]">
                    {patient.status}
                  </span>
                  
                  <div className="flex items-center space-x-3">
                  <button
                    onClick={() => handleViewPatient(patient)}
                      className="p-1 text-[#3D3D3D] hover:text-[#207CE5]"
                      title="View Details"
                  >
                    <Eye className="h-4 w-4" />
                  </button>
                  <button
                      onClick={() => handleEditPatient(patient)}
                      className="p-1 text-[#3D3D3D] hover:text-[#207CE5]"
                      title="Edit"
                    >
                      <Edit3 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleViewHealthTrends(patient)}
                      className="p-1 text-[#3D3D3D] hover:text-[#00BCD4]"
                      title="View Health Trends"
                    >
                      <Activity className="h-4 w-4" />
                  </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-[#F9FBFC] rounded-xl shadow-sm border border-[#DCEAF5] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#DCEAF5]">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#3D3D3D] uppercase tracking-wider">
                    Patient
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#3D3D3D] uppercase tracking-wider">
                    Age/Gender
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#3D3D3D] uppercase tracking-wider">
                    Last Recorded 
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#3D3D3D] uppercase tracking-wider">
                    Recording
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#3D3D3D] uppercase tracking-wider">
                    Calling
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#3D3D3D] uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-[#3D3D3D] uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#DCEAF5]">
                {filteredPatients.map((patient) => (
                  <tr key={patient.id} className="hover:bg-[#DCEAF5] transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-gradient-to-br from-[#207CE5] to-[#4682B4] rounded-full flex items-center justify-center mr-3">
                          <span className="text-white font-bold text-sm">
                            {patient.alias?.split(' ').map(n => n[0]).join('') || 'P'}
                          </span>
                        </div>
                                                 <div>
                           <div className="text-sm font-medium text-[#0B1C2C]">{patient.alias}</div>
                           {/* <div className="text-sm text-[#3D3D3D]">{patient.email}</div> */}
                         </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-[#0B1C2C]">
                        {patient.dob ? `${calculateAge(patient.dob)} years` : 'N/A'}
                      </div>
                      <div className="text-sm text-[#3D3D3D]">{patient.gender || 'N/A'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-[#3D3D3D]">
                      {patientLastRecordedDates[patient.id] 
                        ? new Date(patientLastRecordedDates[patient.id]).toLocaleDateString() 
                        : 'No recordings yet'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button 
                        onClick={() => handleSendLink(patient)}
                        className="px-3 py-1 text-xs font-medium text-white bg-[#207CE5] rounded-lg hover:bg-[#4682B4] transition-colors"
                      >
                        Send Link
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button 
                        onClick={() => handleInitiateCall(patient)}
                        disabled={!patient.phone || callingPatient}
                        className="px-3 py-1 text-xs font-medium text-white bg-[#008080] rounded-lg hover:bg-[#00BCD4] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {callingPatient ? 'Calling...' : 'Call'}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#DCEAF5] text-[#008080]">
                        {patient.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center justify-center space-x-3">
                        <button
                          onClick={() => handleViewPatient(patient)}
                          className="p-1 text-[#3D3D3D] hover:text-[#207CE5]"
                          title="View Details"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleEditPatient(patient)}
                          className="p-1 text-[#3D3D3D] hover:text-[#207CE5]"
                          title="Edit"
                        >
                          <Edit3 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleViewHealthTrends(patient)}
                          className="p-1 text-[#3D3D3D] hover:text-[#00BCD4]"
                          title="View Health Trends"
                        >
                          <Activity className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {filteredPatients.length === 0 && (
        <div className="text-center py-12">
          <UserCheck className="h-12 w-12 text-[#3D3D3D] mx-auto mb-4" />
          <h3 className="text-lg font-medium text-[#0B1C2C] mb-2">No patients found</h3>
          <p className="text-[#3D3D3D]">
            {searchTerm ? 'Try adjusting your search criteria' : 'Get started by adding your first patient'}
          </p>
        </div>
      )}

      {/* Modals */}
      {showModal && <PatientModal />}
      {showSendModal && <SendLinkModal />}
      {showEditModal && <EditPatientModal />}
      {showVitalsModal && selectedPatientForVitals && (
        <PatientVitalsModal 
          patient={selectedPatientForVitals} 
          onClose={() => {
            setShowVitalsModal(false);
            setSelectedPatientForVitals(null);
          }} 
        />
      )}
      {showHealthTrendsModal && selectedPatientForHealthTrends && (
        <PatientHealthTrends 
          patient={selectedPatientForHealthTrends} 
          onClose={() => {
            setShowHealthTrendsModal(false);
            setSelectedPatientForHealthTrends(null);
          }} 
        />
      )}
    </div>
  );
};

export default PatientReview;